import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { ContentCopy, Email, OpenInNew } from '@mui/icons-material';
import { apiService } from '../../../config/api';

export default function UltrahumanInviteDialog({ studyId, onClose, onCreated }: { studyId: string; onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [url, setUrl] = useState('');
  const pending = useRef<{ fingerprint: string; requestId: string }>();
  const create = async (sendEmail: boolean) => {
    if (busy) return;
    const recipient = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) { setError('Enter a valid email address.'); return; }
    setBusy(true); setError(''); setNotice('');
    const fingerprint = JSON.stringify([studyId, recipient, phone.trim(), sendEmail]);
    if (pending.current?.fingerprint !== fingerprint) pending.current = { fingerprint, requestId: crypto.randomUUID() };
    try {
      const result = await apiService.post('/api/ultrahuman/participant-invitations', {
        studyId, email: recipient, phoneNumber: phone.trim() || null, requestId: pending.current.requestId, sendEmail
      });
      const link = new URL(result.invitationUrl);
      if (link.origin !== window.location.origin || link.pathname !== '/ultrahuman/enroll') throw new Error('The invitation link could not be verified.');
      setUrl(link.href); onCreated();
      if (sendEmail && result.delivery === 'accepted') setNotice(`Invitation sent to ${recipient}. You can also copy or open the link below.`);
      else if (sendEmail) setError('The invitation is ready, but email delivery has not been confirmed. Copy or open the link below.');
      else {
        try { await navigator.clipboard.writeText(link.href); setNotice('Invitation link copied. No email was sent.'); }
        catch { setNotice('Invitation created. No email was sent. Use Copy link below.'); }
      }
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not create the invitation.'); }
    finally { setBusy(false); }
  };
  return <Dialog open onClose={() => { if (!busy) onClose(); }} fullWidth maxWidth="sm" aria-labelledby="ultrahuman-invite-title">
    <DialogTitle id="ultrahuman-invite-title">Invite an Ultrahuman participant</DialogTitle>
    <DialogContent dividers>
      <Typography color="text.secondary" sx={{ mb: 2 }}>They will authorize Ultrahuman, complete their profile, and start historical collection for this study.</Typography>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}{notice && <Alert severity="success">{notice}</Alert>}
        <TextField label="Participant email" type="email" value={email} required disabled={busy} onChange={event => { setEmail(event.target.value); setUrl(''); }} />
        <TextField label="Phone number (optional)" value={phone} disabled={busy} onChange={event => setPhone(event.target.value)} />
        {url && <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<ContentCopy />} onClick={() => { void navigator.clipboard.writeText(url).then(() => setNotice('Invitation link copied.')).catch(() => setError('Clipboard access is unavailable. Open the invitation instead.')); }}>Copy link</Button>
          <Button component="a" href={url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNew />} className="ph-no-capture">Open invitation</Button>
        </Box>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose} disabled={busy}>Close</Button>
      <Button onClick={() => { void create(false); }} disabled={busy} startIcon={<ContentCopy />}>Create and copy link</Button>
      <Button onClick={() => { void create(true); }} disabled={busy} variant="contained" startIcon={<Email />}>{busy ? 'Saving…' : 'Send invitation'}</Button>
    </DialogActions>
  </Dialog>;
}
